import * as React from 'react';
import type { Editor } from '@tiptap/react';
import { useState, useEffect, useCallback } from 'react';
import debounce from 'lodash/debounce';

interface EditorContextValue {
   editor: Editor;
}

const EditorContext = React.createContext<EditorContextValue | null>(null);

export const useEditorContext = (): EditorContextValue => {
   const result = React.useContext(EditorContext);
   if (result === null)
      throw Error('Cannot use "useEditorContext" outside of EditorContextProvider');
   return result;
};

function useForceUpdate() {
   const [, setValue] = useState(0);

   return () =>
      requestAnimationFrame(() => {
         requestAnimationFrame(() => {
            setValue(value => value + 1);
         });
      });
}

export const withEditorContext = (Component: () => JSX.Element | null) => {
   return function WithEditorContextComponent(props: { editor: Editor }) {
      const { editor } = props;
      const forceUpdate = useCallback(debounce(useForceUpdate()), []);

      useEffect(() => {
         let isMounted = true;

         // Force a re-render on an update to the editor state.
         editor.on('transaction', () => {
            if (isMounted) {
               forceUpdate();
            }
         });

         return () => {
            isMounted = false;
         };
      }, []);

      return (
         <EditorContext.Provider value={{ editor }}>
            <Component />
         </EditorContext.Provider>
      );
   };
};
